/**
 * SupportPage — 문의 (SC-026).
 *
 * 문의 방식은 **FAQ + 이메일** 두 가지다. 앱 안 1:1 문의는 2026-08-25 에
 * 없어졌다 — 받는 서버가 없고, 문서도 그렇게 확정했다.
 *
 * ── 2026-08-27 전면 검수 ────────────────────────────────────────────
 * 이 화면만 Astryx 밖에 있었다: 아코디언·목록·제목이 전부 raw <div>/<button> +
 * 모듈 CSS 였고, 간격도 12·20px 처럼 사다리 밖 값이었다. 다른 화면과 같은
 * 부품으로 다시 짰다 —
 *   섹션 제목 → Heading level 3   ·   질문/답 → Astryx Collapsible
 *   글 → Text                     ·   여백 → VStack gap (peeka 4배수 사다리)
 * 남은 모듈 CSS 는 **하단 고정 막대** 하나뿐이다. Astryx 에 그 자리가 없다.
 *
 * ⚠ Collapsible 의 터치 타깃은 global.css 가 44px 로 올려 둔다 — 여기서 다시
 *   높이를 주지 말 것.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@astryxdesign/core/Button";
import { Collapsible } from "@astryxdesign/core/Collapsible";
import { Divider } from "@astryxdesign/core/Divider";
import { Heading } from "@astryxdesign/core/Heading";
import { Section } from "@astryxdesign/core/Section";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/Layout";
import { FAQ_CONTENT, type FaqCategory } from "../../data/faqContent";
import { useToast } from "../../components/feedback/Toast";
import { getBuildVersion } from "../../native/liveUpdate";
import styles from "./SupportPage.module.css";

/**
 * 문의 받는 주소.
 * ⚠ 탈퇴 안내(AccountDeletionPage)와 **같은 값이어야 한다** — 두 화면이 다른
 *   주소를 말하면 어디로 보내야 할지 알 수 없다. 운영 CS 주소가 정해지면 함께 바꾼다.
 */
const CS_EMAIL = "support@duckflex.kr";

/**
 * 메일 본문 앞머리 — 앱 버전·기기·OS 를 미리 채운다 (화면정의서 SC-026).
 * 물어보지 않아도 되는 것을 사용자에게 묻지 않기 위해서다.
 */
function buildMailto(
  subject: string,
  notice: string,
  labels: { appVersion: string; device: string },
): string {
  const lines = [
    "",
    "",
    "──────────────",
    notice,
    `${labels.appVersion}: ${getBuildVersion()}`,
    `${labels.device}: ${typeof navigator !== "undefined" ? navigator.userAgent : "-"}`,
  ];
  return (
    `mailto:${CS_EMAIL}` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(lines.join("\n"))}`
  );
}

export default function SupportPage() {
  const { t } = useTranslation("me");
  const toast = useToast();
  /* 처음엔 아무것도 펼치지 않는다 — 카테고리를 훑고 고르는 자리라 하나가 열려
     있으면 그 답이 먼저 눈에 들어와 목록이 가려진다. */
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  /**
   * 메일 앱으로 넘긴다. 없는 기기(데스크탑 브라우저 등)에서는 창이 열리지 않으므로
   * 주소를 클립보드로 넘기고 알린다 — 눌렀는데 아무 일도 안 나는 게 제일 나쁘다.
   */
  const handleEmail = async () => {
    const href = buildMailto(t("support.mailSubject"), t("support.mailNotice"), {
      appVersion: t("support.mailAppVersion"),
      device: t("support.mailDevice"),
    });
    const before = Date.now();
    window.location.href = href;
    // 메일 앱이 뜨면 이 탭은 잠시 뒤로 물러난다. 그대로 남아 있으면 못 연 것으로 본다.
    window.setTimeout(() => {
      if (document.hidden || Date.now() - before > 1500) return;
      void navigator.clipboard
        ?.writeText(CS_EMAIL)
        .then(() => toast.success(t("support.mailCopied", { email: CS_EMAIL })))
        .catch(() => toast.info(t("support.mailAddress", { email: CS_EMAIL })));
    }, 600);
  };

  return (
    <Section variant="transparent" padding={4} maxWidth={720} width="100%" minHeight="100%">
      <VStack gap={6}>
        {FAQ_CONTENT.map((group: FaqCategory) => (
          <VStack key={group.code} gap={2}>
            {/* 카테고리 이름 — **액센트 색**에 h5 (2026-08-27).
                h3 는 이 화면에서 가장 큰 글자라 질문(16px)과 크기가 붙어
                '제목 → 항목' 이 안 갈렸다. 크기를 두 단 낮추고 색으로 세운다:
                작아도 색이 다르면 층이 보인다. */}
            <Heading level={5} color="accent">{group.name}</Heading>
            {group.items.map((item, i) => (
              <VStack key={item.id} gap={2}>
                {i > 0 && <Divider />}
                <Collapsible
                  isOpen={openFaq === item.id}
                  onOpenChange={(open) => setOpenFaq(open ? item.id : null)}
                  trigger={<Text weight="medium">{item.q}</Text>}
                >
                  {/* 답은 문단으로 나눈다. 예전에는 어드민이 넣은 HTML 을 그대로
                      심었지만(dangerouslySetInnerHTML), 이제 글이 앱 안에 있어
                      그럴 이유가 없다 — 태그를 심지 않으면 살균도 필요 없다. */}
                  <VStack gap={2} align="start">
                    {item.a.split("\n").map((line, li) => (
                      <Text key={li} type="supporting" color="secondary">
                        {line}
                      </Text>
                    ))}
                  </VStack>
                </Collapsible>
                {/* 카테고리의 마지막 질문 아래에도 선을 긋는다 — 다음 카테고리
                    제목과 맞닿는 자리라, 선이 없으면 두 묶음이 한 덩어리로 붙는다 */}
                {i === group.items.length - 1 && <Divider />}
              </VStack>
            ))}
          </VStack>
        ))}

        {/* 이메일 문의 — 목록 끝에 붙는다. 하단 고정 막대는 스크롤을 가로막는
            띠 하나를 늘 얹어 두는데, FAQ 를 훑는 화면에서는 그 띠가 답을
            가린다 (2026-08-27). 다 읽고 그래도 안 풀렸을 때 만나는 자리가 맞다. */}
        <div className={styles.mailBlock}>
          <VStack gap={2}>
            <Button
              variant="secondary"
              size="lg"
              label={t("support.mailAction")}
              clickAction={() => void handleEmail()}
              width="100%"
            />
            {/* 기다리게 될 수 있다는 걸 미리 말한다 — 답이 늦는 것보다
                늦을 줄 몰랐던 것이 나쁘다. */}
            <Text type="supporting" color="secondary">
              {t("support.mailHint")}
            </Text>
          </VStack>
        </div>
      </VStack>
    </Section>
  );
}
